import { Button, InputAdornment, Stack, TextField } from '@mui/material';
import DnsProviderLogo from 'components/shared/DnsProviderLogo';
import { IdPCommonInputFieldName } from 'consts';
import { useDialogsContext } from 'contexts/Dialogs/DialogsContext';
import { useField } from 'contexts/SSOConfigFieldsContext';
import { useToast } from 'hooks';
import { useDebouncedDomainProviderGuide } from 'hooks/useDomainProviderGuide';
import React, { ChangeEventHandler, useCallback, useState } from 'react';
import { ToastSeverity } from 'types/toast';

const VerifyDomainInput: React.FC = () => {
	const [domain, setDomain] = useState('');

	const { value, setValue } = useField(IdPCommonInputFieldName.SSODomains);
	const { addToast } = useToast();

	const onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> =
		useCallback((e) => {
			setDomain(e.target.value.replaceAll(' ', ''));
		}, []);

	const disabled = !domain.length;

	const { openVerifyDomainDialog } = useDialogsContext();

	const handleVerify = useCallback(async () => {
		const res = await openVerifyDomainDialog(domain);
		if (res.success) {
			addToast({
				content: intl.tr('domainVerification.dialog.successToast'),
				severity: ToastSeverity.Success
			});
			setDomain('');
			if (!((value as string[]) || []).includes(domain)) {
				setValue([...((value as string[]) || []), domain]);
			}
		}
		// cancel
	}, [addToast, domain, openVerifyDomainDialog, setValue, value]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			/* istanbul ignore next */
			if (e.key === 'Enter' && !disabled) {
				handleVerify();
				return;
			}
			/* istanbul ignore next */
			if (e.key === ' ') {
				e.preventDefault();
			}
		},
		[disabled, handleVerify]
	);

	const { data: dnsProvider } = useDebouncedDomainProviderGuide(domain);

	return (
		<Stack gap={2} alignItems="center" direction="row">
			<TextField
				sx={{ flexGrow: 1 }}
				value={domain}
				onChange={onChange}
				onKeyDown={onKeyDown}
				required
				size="small"
				data-testid="verify-domain"
				placeholder={intl.tr('ssoConfig.fields.verifyDomain.placeholder')}
				slotProps={{
					input: {
						endAdornment: dnsProvider ? (
							<InputAdornment
								position="end"
								data-testid="dns-provider-adornment"
							>
								{dnsProvider && <DnsProviderLogo dnsProvider={dnsProvider} />}
							</InputAdornment>
						) : null
					}
				}}
			/>
			<Button variant="outlined" disabled={disabled} onClick={handleVerify}>
				{intl.tr('ssoConfig.fields.verifyDomain.verifyButton')}
			</Button>
		</Stack>
	);
};

export default VerifyDomainInput;
